/**
 * Eine Materialzeile am Projekt — Menge, Lieferant, Stand, entfernen.
 * Die Werte selbst sind die Knöpfe (Benjamin, 02.10.2026: „nicht alles doppelt"):
 * [20 Tonne] öffnet die Menge, [Lieferant] den Wechsel — kein Infotext plus
 * gleichnamiger Knopf daneben.
 * Aus ProjektMaterial.tsx herausgelöst (02.10.2026), weil die Sektion jetzt
 * drei Ansichten hat (nach Lieferant, Material, Lieferscheine) und dieselbe
 * Zeile in zweien davon steht.
 */
import { useState } from "react";

import {
  useMaterialLieferanten, useProjektMaterialAendern, type ProjektMaterialZeile,
} from "../../hooks/useLieferanten";
import LieferantSuche from "../lieferanten/LieferantSuche";
import { StandChip, StandPanel } from "./AnfrageStand";
import "../../styles/lieferanten.css";

/** mitLieferant: in der Material-Ansicht steht der Lieferant in der Zeile (dort gibt es keine Gruppe). */
export default function MaterialZeile({ m, projektId, standOffen, onStand, onAnfrage, mitLieferant = false }: {
  m: ProjektMaterialZeile; projektId: string; standOffen: boolean; onStand: () => void; onAnfrage: () => void;
  mitLieferant?: boolean;
}) {
  const a = useProjektMaterialAendern(projektId);
  const [modus, setModus] = useState<"" | "menge" | "lieferant" | "weg">("");
  const [menge, setMenge] = useState(m.menge);
  const [einheit, setEinheit] = useState(m.einheit);
  const umschalten = (z: typeof modus) => setModus(modus === z ? "" : z);
  // Nur eine Einheit ohne Zahl ist keine Menge („t" allein sagte nichts).
  const mengeText = m.menge ? [m.menge, m.einheit].filter(Boolean).join(" ") : "";
  const info = [m.artikelnummer ? `Art.-Nr. ${m.artikelnummer}` : "",
    m.abweichend ? `statt Standard${m.standard_name ? ` (${m.standard_name})` : ""}` : ""].filter(Boolean).join(" · ");
  // Der Lieferant steht als Wert in der Zeile, wo ihn keine Gruppe schon nennt;
  // in „Nach Lieferant“ genügt ein kleines ⇄ zum Wechseln.
  const lieferantAlsWert = mitLieferant || !m.lieferant_id;
  return (
    <li className="lf-zeile lf-mzeile">
      <div className="lf-zeile-haupt">
        <span className="lf-zeile-name">{m.name}{m.art === "maschine" && <em className="lf-art">Maschine</em>}</span>
        {info && <span className="lf-zeile-info">{info}</span>}
      </div>
      <div className="lf-mwerte">
        <button type="button" className={"lf-wert" + (mengeText ? "" : " ist-leer")} aria-expanded={modus === "menge"}
          title="Menge ändern" onClick={() => umschalten("menge")}>
          {mengeText || "Menge offen"}
        </button>
        {lieferantAlsWert ? (
          <button type="button" className={"lf-wert" + (m.lieferant_id ? "" : " ist-leer") + (m.abweichend ? " ist-abweichend" : "")}
            aria-expanded={modus === "lieferant"} title="Lieferant wechseln" onClick={() => umschalten("lieferant")}>
            {m.lieferant_name || "Lieferant wählen"}
          </button>
        ) : (
          <button type="button" className="lf-wert lf-wert-icon" aria-expanded={modus === "lieferant"}
            aria-label={`Lieferant für ${m.name} wechseln`} title="Lieferant wechseln" onClick={() => umschalten("lieferant")}>
            ⇄
          </button>
        )}
        <StandChip m={m} offen={standOffen} onClick={onStand} />
        <button type="button" className="lf-wert lf-wert-icon" aria-label={`${m.name} entfernen`} title="Aus dem Projekt nehmen"
          onClick={() => umschalten("weg")}>×</button>
      </div>
      {standOffen && (
        <div className="lf-zeile-unter">
          <StandPanel m={m} projektId={projektId} onAnfrage={onAnfrage} />
        </div>
      )}
      {modus === "menge" && (
        <div className="lf-form lf-zeile-unter">
          <div className="lf-form-zwei">
            <label className="lf-label">Menge<input className="lf-feld" inputMode="decimal" value={menge} onChange={(e) => setMenge(e.target.value)} /></label>
            <label className="lf-label">Einheit<input className="lf-feld" value={einheit} onChange={(e) => setEinheit(e.target.value)} /></label>
          </div>
          <div className="lf-knoepfe">
            <button type="button" className="lf-knopf" onClick={() => setModus("")}>Abbrechen</button>
            <button type="button" className="lf-knopf is-primaer" disabled={a.aendern.isPending}
              onClick={() => a.aendern.mutate({ id: m.id, menge, einheit }, { onSuccess: () => setModus("") })}>Speichern</button>
          </div>
        </div>
      )}
      {modus === "lieferant" && (
        <div className="lf-zeile-unter">
          <LieferantWahl m={m} onWahl={(id, alsStandard) => a.aendern.mutate({ id: m.id, lieferant_id: id, als_standard: alsStandard }, { onSuccess: () => setModus("") })} onAbbruch={() => setModus("")} />
        </div>
      )}
      {modus === "weg" && (
        <div className="lf-knoepfe lf-zeile-unter">
          <span className="lf-hinweis">„{m.name}“ aus dem Projekt nehmen? Im Angebot bleibt alles, wie es ist.</span>
          <button type="button" className="lf-knopf" onClick={() => setModus("")}>Nein</button>
          <button type="button" className="lf-knopf is-gefahr" disabled={a.entfernen.isPending} onClick={() => a.entfernen.mutate(m.id)}>Entfernen</button>
        </div>
      )}
    </li>
  );
}

/**
 * Lieferant für eine Materialzeile: Standard, die es führen, sonst Suche über alle.
 *
 * „Als Standard merken" (Benjamin, 25.09.2026): der Gewählte ist dann beim
 * nächsten Projekt vorausgewählt. Hat das Material noch keinen Standard, ist
 * der Haken gesetzt — so bekommt jedes Material mit der Zeit seinen.
 */
function LieferantWahl({ m, onWahl, onAbbruch }: { m: ProjektMaterialZeile; onWahl: (id: string, alsStandard: boolean) => void; onAbbruch: () => void }) {
  const fuehren = useMaterialLieferanten(m.material_id);
  const [alle, setAlle] = useState(false);
  const [merken, setMerken] = useState(!m.standard_id);
  const liste = fuehren.data?.lieferanten ?? [];
  const waehlen = (id: string) => onWahl(id, merken && !!id);
  return (
    <div className="lf-lwahl">
      <label className="lf-merken">
        <input type="checkbox" checked={merken} onChange={(e) => setMerken(e.target.checked)} />
        <span>Gewählten Lieferanten als Standard für „{m.name}“ merken{m.standard_name ? ` (bisher ${m.standard_name})` : ""}</span>
      </label>
      <button type="button" className={"lf-mwahl-option" + (!m.abweichend ? " ist-aktiv" : "")} onClick={() => waehlen("")} disabled={!m.standard_id && !m.abweichend}>
        <span className="lf-mwahl-name">Standard{m.standard_name ? ` · ${m.standard_name}` : ""}</span>
        <span className="lf-mwahl-info">{m.standard_id ? "gilt in jedem Projekt, solange hier nichts anderes gewählt ist" : "noch kein Standardlieferant hinterlegt"}</span>
      </button>
      {liste.filter((l) => !l.standard).map((l) => (
        <button key={l.id} type="button" className={"lf-mwahl-option" + (m.lieferant_id === l.id && m.abweichend ? " ist-aktiv" : "")} onClick={() => waehlen(l.id)}>
          <span className="lf-mwahl-name">{l.name}</span>
          <span className="lf-mwahl-info">{[l.artikelnummer ? `Art.-Nr. ${l.artikelnummer}` : "", l.preis_netto != null ? `${l.preis_netto.toLocaleString("de-DE", { style: "currency", currency: "EUR" })} netto` : "", "führt das Material"].filter(Boolean).join(" · ")}</span>
        </button>
      ))}
      {alle ? (
        <LieferantSuche ausser={[m.lieferant_id]} onWahl={waehlen} onAbbruch={() => setAlle(false)}
          hinweis="Wer hier gewählt wird, bekommt das Material auch in seine Liste." />
      ) : (
        <div className="lf-knoepfe">
          <button type="button" className="lf-knopf" onClick={onAbbruch}>Abbrechen</button>
          <button type="button" className="lf-knopf" onClick={() => setAlle(true)}>Anderen Lieferanten suchen …</button>
        </div>
      )}
    </div>
  );
}
